import InputWithLabel from "app/components/FormInputs/components/InputWithLabel/InputWithLabel";
import { Countries, Country } from "app/types/registrationTypes";
import { registrationInputMaxLength } from "app/utils/const";
import { useEffect, useState } from "react";
import { FormattedMessage, IntlShape } from "react-intl";
import CanadianStateSelect from "./components/CanadianStateSelect/CanadianStateSelect";

type Props = {
  intl: IntlShape;
  country: Country;
  state: string;
  handleChange: (id: string, value: string) => void;
  countries: Countries;
  countryValidationError: boolean;
  stateValidationError: boolean;
  setIsCanadaSelected: (isCanadaSelected: boolean) => void;
  isCanadaSelected: boolean;
};

const CountryState = ({
  intl,
  country,
  state,
  handleChange,
  countries,
  countryValidationError,
  stateValidationError,
  setIsCanadaSelected,
  isCanadaSelected,
}: Props) => {
  // Using a constant instead of the magic number
  const CANADA_COUNTRY_ID = "11";
  const [errorCountry, setErrorCountry] = useState<string>("");
  const [errorState, setErrorState] = useState<string>("");

  const validateCountry = (id: string, value: string) => {
    if (value.length < 1) {
      setErrorCountry(
        intl.formatMessage({
          id: "Required",
        })
      );
    } else {
      setErrorCountry("");
    }
  };

  const validateState = (id: string, value: string) => {
    if (value.length < 2) {
      setErrorState(
        intl.formatMessage({
          id: "Required",
        })
      );
    } else {
      setErrorState("");
    }
  };

  useEffect(() => {
    if (countryValidationError) {
      validateCountry("country", "");
    }
    if (stateValidationError) {
      validateState("state", "");
    }
    if (country === CANADA_COUNTRY_ID && state !== "") {
      validateState("state", state);
    }
  }, [countryValidationError, stateValidationError, state]);

  useEffect(() => {
    if (country === CANADA_COUNTRY_ID) {
      setIsCanadaSelected(true);
    } else {
      setIsCanadaSelected(false);
    }
    if (country) {
      // Find the country object by its ID
      const selectedCountry = countries.find(c => c.id.toString() === country);
      // If the country is found, update the phoneNumberPrefixId
      if (selectedCountry) {
        // dependency where country can set the phone number prefix
        // (when country selected in the personal details form)
        // changing the phone number prefix can also be set independent of country.
        // phoneNumberPrefixId is used to track selected the phone number prefix
        handleChange("phoneNumberPrefixId", selectedCountry.id);
      } else {
        console.warn(
          `Country with ID ${country} not found in countries array.`
        );
      }
    }
  }, [country, setIsCanadaSelected]);

  return (
    <div className="form-details__row form-details__row--two-cols">
      <div className="form-details__col">
        <div>
          <FormattedMessage tagName="span" id="settings-country" />
          <span className="required">*</span>
        </div>
        <div className="form-details__row--input">
          <select
            className={`${errorCountry ? "error" : ""}`}
            onChange={e => handleChange("country", e.target.value)}
            onBlur={e => validateCountry("country", e.target.value)}
            id="country"
            value={country}
          >
            {
              <option value={0}>
                <FormattedMessage id="home-register-select-country" />
              </option>
            }
            {countries &&
              countries.map((_country, index) => (
                <option
                  key={_country.id}
                  value={_country.id}
                  data-countrycode={_country.code}
                >
                  {_country["country-name"]}
                </option>
              ))}
          </select>
        </div>
        {errorCountry && <span className="error-message">{errorCountry}</span>}
      </div>
      <div className="form-details__col">
        {isCanadaSelected ? (
          <CanadianStateSelect
            intl={intl}
            value={state}
            onChange={value => handleChange("state", value)}
            error={errorState}
          />
        ) : (
          <InputWithLabel
            label={intl.formatMessage({
              id: "home-register-select-state",
            })}
            inputId={"state"}
            value={state}
            onValueChange={handleChange}
            key={"state"}
            type={"text"}
            required={true}
            maxLength={registrationInputMaxLength.emailMaxLength}
            placeholder={intl.formatMessage({
              id: "home-register-select-state",
            })}
            validateInput={true}
            error={errorState}
            handleValidation={validateState}
          />
        )}
      </div>
    </div>
  );
};

export default CountryState;
